import {TKey} from '@/translations/i18n'
import {stringOrDate, View} from 'react-big-calendar'
import {palette} from '@/theme/palette'
import {
    AgendaEventType,
    AgendaEventStatus,
    AgendaEntity,
    AgendaView,
    AgendaDateRange,
    EventRoom,
    EventTypeKeyName,
    EventCategory,
    RepetitionReturn,
    RepetitionOptions
} from '@/features/agenda/types'
import dayjs, {OpUnitType} from 'dayjs'
import {ObjectKeys, raise} from '@/utilities/helpers'
import {
    AddConsultantAbsenceConfirmationValidationSchema,
    AddPatientAbsenceConfirmationValidationSchema,
    CreateEventFormConfirmationValidationSchema,
    EditEventFormConfirmationValidationSchema,
    EditGenericEventFormConfirmationValidationSchema
} from '@/features/agenda/formSchemas'
import {EventPayload, HttpCreateEventOptions, HttpEditEventOptions} from '@/features/agenda/http/agenda.http'
import {DEFAULT_END_TIME, DEFAULT_START_TIME} from '../users-availability/utils'
import {formatDate, formatLocaleDate} from '@/dayjs'
import {TOptions} from 'i18next'
import {AccessGroupNameEnum, AccessType} from '@/features/access-types/types'

export const agendaAvailableViews: View[] = ['day', 'week', 'month', 'agenda']
export const agendaViewMapToLabel = {
    day: 'agenda:day',
    week: 'agenda:week',
    month: 'agenda:month',
    agenda: 'agenda:plan'
} as const satisfies Record<AgendaView, TKey>

export const agendaTargetMapToLabel = {
    users: 'commons:operators',
    patients: 'commons:patients'
} as const satisfies Record<AgendaEntity, TKey>

export const agendaEventStatusMapToLabel = {
    provisional: 'agenda:appointment_status_provisional',
    scheduled: 'agenda:appointment_status_scheduled',
    completed: 'agenda:appointment_status_completed',
    missed: 'agenda:appointment_status_missed',
    to_reschedule: 'agenda:appointment_status_to_reschedule',
    cancelled: 'agenda:appointment_status_cancelled',
    withdrawn: 'agenda:appointment_status_withdrawn',
    to_be_confirmed: 'agenda:appointment_status_to_be_confirmed',
    tentative: 'agenda:appointment_status_tentative'
} as const satisfies Record<AgendaEventStatus, TKey>

export const agendaEventStatusMapToColor = {
    provisional: palette.light.orange['500'],
    scheduled: palette.light.primary['400'],
    completed: palette.light.green['500'],
    missed: palette.light.red['500'],
    withdrawn: palette.light.warning['500'],
    to_reschedule: palette.light.primary['400'],
    cancelled: palette.light.purple['400'],
    to_be_confirmed: palette.light.neutral['400'],
    tentative: palette.light.neutral['300']
} as const satisfies Record<AgendaEventStatus, string>

export const agendaEventStatusMapToDarkColor = {
    provisional: palette.light.orange['800'],
    scheduled: palette.light.primary['800'],
    completed: palette.light.green['800'],
    missed: palette.light.red['800'],
    withdrawn: palette.light.danger['700'],
    to_reschedule: palette.light.primary['800'],
    cancelled: palette.light.purple['800'],
    to_be_confirmed: palette.light.neutral['800'],
    tentative: palette.light.neutral['800']
} as const satisfies Record<AgendaEventStatus, string>

export const agendaEventStatusMapToBackgroundColor = {
    provisional: palette.light.orange['50'],
    scheduled: palette.light.primary['100'],
    completed: palette.light.green['50'],
    withdrawn: palette.light.warning['50'],
    missed: palette.light.red['50'],
    to_reschedule: palette.light.neutral['white'],
    cancelled: palette.light.purple['50'],
    to_be_confirmed: palette.light.neutral['50'],
    tentative: palette.light.neutral['50']
} as const satisfies Record<AgendaEventStatus, string>

export const appointmentCategoryMapToLabel = {
    treatments: 'commons:treatment',
    project_management: 'commons:manage_project',
    team_meeting: 'agenda:appointment_type_team_meeting_glo',
    evaluations: 'agenda:appointment_type_evaluation',
    specialist_visits: 'agenda:appointment_type_specialist_visit'
} as const satisfies Record<EventCategory, TKey>

export const eventTypeMapToLabel = {
    cardiac_holter: 'agenda:access_cardiac_holter',
    cardiology_visit: 'agenda:access_cardiology_visit',
    certification: 'agenda:access_certification',
    cognitive_neuropsych_rehab: 'agenda:access_cognitive_neuropsychological_rehabilitation',
    counseling: 'agenda:access_counseling',
    cycle_closing_processing: 'agenda:access_cycle_closure_processing',
    cycle_opening_processing: 'agenda:access_cycle_opening_processing',
    educational_intervention: 'agenda:access_educational_intervention',
    educator_evaluation: 'medical_records:type_professional_educator_evaluation',
    orl_visit: 'agenda:access_orl_visit',
    extension_processing: 'agenda:access_extension_processing',
    glo_meeting: 'agenda:access_glo_meeting',
    initial_project_evaluation: 'agenda:access_initial_project_evaluation',
    initial_social_service_eval: 'agenda:access_initial_social_service_evaluation',
    intermediate_project_evaluation: 'agenda:access_intermediate_project_evaluation',
    neurological_specialist_visit: 'agenda:access_neurological_specialist_visit',
    neuromotor_evaluation: 'agenda:access_neuromotor_evaluation',
    neuromotor_rehab: 'agenda:access_neuromotor_rehabilitation',
    neuropsychiatric_specialist_visit: 'agenda:access_neuropsychiatric_specialist_visit',
    nutritional_visit: 'agenda:access_nutritional_visit',
    occupational_therapy: 'agenda:access_occupational_therapy',
    occupational_therapy_evaluation: 'agenda:access_occupational_therapy_evaluation',
    orthopedic_visit: 'agenda:access_orthopedic_visit',
    parent_training: 'agenda:access_parent_training',
    pediatric_visit: 'agenda:access_pediatric_visit',
    physiatric_specialist_visit: 'agenda:access_physiatric_specialist_visit',
    project_closing_processing: 'agenda:access_project_closure_processing',
    project_opening_processing: 'agenda:access_project_opening_processing',
    project_renewal_processing: 'agenda:access_project_renewal_processing',
    psychological_evaluation: 'agenda:access_psychological_evaluation',
    psychological_therapy: 'agenda:access_psychological_therapy',
    psychological_therapy_family: 'agenda:access_psychological_therapy_family',
    psychomotor_evaluation: 'agenda:access_psychomotor_evaluation',
    psychomotor_therapy: 'agenda:access_psychomotor_rehabilitation',
    pulmonology_visit: 'agenda:access_pneumology_visit',
    short_extension_processing: 'agenda:access_short_extension_processing',
    short_team_meeting: 'agenda:access_short_team_meeting',
    social_service_evaluation: 'agenda:access_social_service_evaluation',
    speech_evaluation: 'agenda:access_speech_therapy_evaluation',
    speech_therapy: 'agenda:access_speech_therapy',
    team_meeting: 'agenda:access_team_meeting',
    update_medical_record: 'agenda:access_update_medical_record',
    medical_interview: 'agenda:access_medical_interview',
    therapy_evaluation: 'agenda:access_therapy_evaluation',
    project_management_and_revision_sir: 'agenda:access_project_management_and_revision_sir',
    team_meeting_sir: 'agenda:access_team_meeting_sir',
    observation_and_evaluation_sir: 'agenda:access_observation_and_evaluation_sir',
    observation_and_evaluation_sir_tr: 'agenda:access_observation_and_evaluation_sir_tr',
    observation_and_evaluation_sir_mrp: 'agenda:access_observation_and_evaluation_sir_mrp'
} as const satisfies Record<EventTypeKeyName, TKey>

export const absenceTypeToLabel = {
    patient_absence: 'agenda:patient_absence',
    consultant_absence: 'agenda:consultant_absence',
    unavailability: 'agenda:unavailability'
} as const satisfies Record<Exclude<AgendaEventType['type'], 'event' | 'generic_event'>, TKey>

export const locationMapToLabel = {
    gym: 'agenda:room_gym',
    clinic: 'agenda:clinic',
    domiciliary: 'agenda:domiciliary'
} as const satisfies Record<EventRoom, TKey>

export const agendaDurationMapToLabel = Object.fromEntries(
    [15, 30, 45, 60, 75, 90, 105, 120].map(duration => [duration, 'commons:duration_minutes'])
) as Record<15 | 30 | 45 | 60 | 75 | 90 | 105 | 120, TKey>

export const accessTypeKeyNameToDefaultDuration = {
    project_opening_processing: 60,
    project_renewal_processing: 60,
    project_closing_processing: 60,
    cycle_opening_processing: 60,
    cycle_closing_processing: 60,
    initial_project_evaluation: 15,
    extension_processing: 60,
    short_extension_processing: 15,
    intermediate_project_evaluation: 60,
    cardiac_holter: 60,
    certification: 60,
    counseling: 60,
    glo_meeting: 60,
    neurological_specialist_visit: 60,
    neuropsychiatric_specialist_visit: 60,
    physiatric_specialist_visit: 60,
    cardiology_visit: 60,
    nutritional_visit: 60,
    orl_visit: 60,
    orthopedic_visit: 60,
    pediatric_visit: 60,
    pulmonology_visit: 60,
    psychological_therapy: 60,
    psychological_therapy_family: 60,
    parent_training: 60,
    initial_social_service_eval: 60,
    social_service_evaluation: 60,
    team_meeting: 60,
    cognitive_neuropsych_rehab: 60,
    educational_intervention: 60,
    educator_evaluation: 60,
    neuromotor_evaluation: 60,
    neuromotor_rehab: 60,
    occupational_therapy: 60,
    occupational_therapy_evaluation: 60,
    psychological_evaluation: 60,
    psychomotor_evaluation: 60,
    psychomotor_therapy: 60,
    speech_evaluation: 60,
    speech_therapy: 60,
    short_team_meeting: 15,
    update_medical_record: 60,
    medical_interview: 60,
    therapy_evaluation: 60,
    project_management_and_revision_sir: 60,
    team_meeting_sir: 60,
    observation_and_evaluation_sir: 60,
    observation_and_evaluation_sir_tr: 60,
    observation_and_evaluation_sir_mrp: 60
} as const satisfies Record<EventTypeKeyName, number>

export const validateAccessTypeCompatibility = (
    projectType?: string | null,
    accessType?: string,
    status?: AgendaEventStatus | null
): boolean => {
    // Check for incompatible combinations
    return (
        (!projectType &&
            accessType === 'project_opening_processing' &&
            status != 'provisional' &&
            status != 'scheduled') ||
        (!projectType &&
            accessType === 'project_closing_processing' &&
            status != 'provisional' &&
            status != 'scheduled')
    )
}

export const hasOverlap = ({
    start,
    end,
    blockouts,
    resourceId
}: {
    start: stringOrDate
    end: stringOrDate
    blockouts: AgendaEventType[]
    resourceId?: string
}) => {
    // Check if the new time slot overlaps with any background event
    const result = blockouts.some(blockout => {
        if (resourceId && blockout.resourceId !== resourceId) {
            return false
        }

        const blockoutStart = dayjs(blockout.start)
        const blockoutEnd = dayjs(blockout.end)
        const newStart = dayjs(start)
        const newEnd = dayjs(end)

        return (
            (newStart.isAfter(blockoutStart) && newStart.isBefore(blockoutEnd)) ||
            (newEnd.isAfter(blockoutStart) && newEnd.isBefore(blockoutEnd)) ||
            newStart.isSame(blockoutStart) ||
            newEnd.isSame(blockoutEnd)
        )
    })

    return result
}

// Todo: Check this function after the backend is updated
export const getEventResource = (target: AgendaEntity, event: AgendaEventType) => {
    if (target == 'users' && event.type != 'unavailability' && event.type != 'generic_event' && event.eventPatients) {
        return {
            id: event.eventPatients[0].patientId,
            fullName: event.eventPatients[0].patient?.fullName
        }
    }

    if (
        target == 'patients' &&
        event.type != 'unavailability' &&
        event.type != 'generic_event' &&
        event.eventConsultants
    ) {
        return {
            id: event.eventConsultants[0].userId,
            fullName: event.eventConsultants[0].user.fullName,
            specialization: event.eventConsultants[0].user.specialization
        }
    }
}

/**
 * Checks if a date is today
 */
export const isToday = (date: Date): boolean => {
    return dayjs(date).isSame(dayjs(), 'day')
}

/**
 * Maps AgendaView types to dayjs OpUnitType values
 * @param view - The agenda view type
 * @returns The corresponding dayjs OpUnitType
 */
export const viewToDayjsUnit = (view: AgendaView): OpUnitType => {
    switch (view) {
        case 'day':
            return 'day'
        case 'week':
            return 'week'
        case 'month':
            return 'month'
        case 'agenda':
            return 'month'
        default:
            return raise(`Invalid view: ${view}`)
    }
}

/**
 * Calculates start and end dates based on a reference date and view type
 * @param date - Date string in DD-MM-YYYY format
 * @param view - Type of agenda view (day, week, month, agenda)
 * @returns Object containing startDate and endDate in YYYY-MM-DD format
 */
export const calculateAgendaDates = (date: string | undefined, view: AgendaView | undefined): AgendaDateRange => {
    if (!date || !view) {
        return {startDate: '', endDate: ''}
    }

    try {
        const validDate = dayjs(date, 'DD-MM-YYYY')

        // Check if the date is valid
        if (!validDate.isValid()) {
            return {startDate: '', endDate: ''}
        }

        const viewUnit = viewToDayjsUnit(view)

        const startDate = validDate.startOf(viewUnit).format('YYYY-MM-DD')
        const endDate = validDate.endOf(viewUnit).format('YYYY-MM-DD')

        return {startDate, endDate}
    } catch (error) {
        // Handle any unexpected errors gracefully
        console.error('Error calculating agenda dates:', error)
        return {startDate: '', endDate: ''}
    }
}

/**
 * Calculates the week position in month (1-5) safely
 * @param dayOfMonth - The day of the month (1-31)
 * @returns The week position (1-5)
 */
export const getWeekPositionInMonth = (dayOfMonth: number): 1 | 2 | 3 | 4 | 5 => {
    // Use a lookup table to ensure type safety without assertions
    const weekPositions = [1, 2, 3, 4, 5] as const
    const position = Math.min(Math.ceil(dayOfMonth / 7), 5)

    // Since position is guaranteed to be 1-5, we can safely index into our const array
    return weekPositions[position - 1]
}

export const generateFrequencyRepetitionId = (frequency: RepetitionOptions, day: string, position: number) => {
    return `${frequency == 'lastDayOfMonth' ? 'monthly' : frequency}-${day}-${frequency == 'lastDayOfMonth' ? 5 : position}`
}

/**
 * Counts how many times a specific day of the week occurs in a given month
 * @param date - The date to check the month for
 * @param dayOfWeek - The day of the week to count (0-6, where 0 is Sunday)
 * @returns The number of occurrences of that day in the month
 */
const countDayOccurrencesInMonth = (date: Date, dayOfWeek: number): number => {
    const startOfMonth = dayjs(date).startOf('month')
    const endOfMonth = dayjs(date).endOf('month')
    let count = 0
    let currentDate = startOfMonth

    while (currentDate.isBefore(endOfMonth) || currentDate.isSame(endOfMonth, 'day')) {
        if (currentDate.day() === dayOfWeek) {
            count++
        }
        currentDate = currentDate.add(1, 'day')
    }

    return count
}

/**
 * Maps position to ordinal number
 * @param position - The position to map
 * @returns The ordinal number
 */
export const positionToOrdinalNumber = {
    1: 'commons:number_first',
    2: 'commons:number_second',
    3: 'commons:number_third',
    4: 'commons:number_fourth',
    5: 'commons:number_fifth'
} as const satisfies Record<1 | 2 | 3 | 4 | 5, TKey>

/**
 * Generates repetition options for scheduling based on day and position
 * @param date - The date to calculate repetition options for
 * @param t - Translation function for internationalization
 * @returns Object containing repetition options with translated labels
 */
export const generateRepetitionOptions = (
    date: Date,
    t: (key: TKey, options?: TOptions) => string
): RepetitionReturn => {
    const day = formatDate(date, 'dddd').toLowerCase()
    const localeDay = formatLocaleDate(date, 'dddd')

    // Calculate the week position in the month (1-5)
    const dayOfMonth = dayjs(date).date()
    const weekPositionInMonth = getWeekPositionInMonth(dayOfMonth)

    const frequencyToLabel = {
        weekly: 'agenda:schedule_weekly_repetition',
        monthly: 'agenda:schedule_monthly_repetition',
        lastDayOfMonth: 'agenda:schedule_last_day_of_month'
    } as const satisfies Record<RepetitionOptions, TKey>

    const generateOption = (frequency: RepetitionOptions): RepetitionReturn => {
        const position = frequency == 'weekly' ? 1 : weekPositionInMonth

        return {
            [frequency]: {
                label: t(frequencyToLabel[frequency], {
                    day: localeDay,
                    position: position < 5 ? t(positionToOrdinalNumber[position]).toLowerCase() : undefined
                }),
                value: {
                    id: generateFrequencyRepetitionId(frequency, day, position),
                    day,
                    localeDay,
                    position: frequency == 'lastDayOfMonth' ? 5 : position,
                    frequency: frequency == 'lastDayOfMonth' ? 'monthly' : frequency
                }
            }
        }
    }

    // Only add lastDayOfMonth option when position is 4 or 5 (last week of month)
    switch (weekPositionInMonth) {
        case 4: {
            // Get the day of the week (0-6, where 0 is Sunday)
            const dayOfWeek = dayjs(date).day()
            // Count how many times this day occurs in the month
            const dayOccurrences = countDayOccurrencesInMonth(date, dayOfWeek)

            return dayOccurrences > 4
                ? {
                      ...generateOption('weekly'),
                      ...generateOption('monthly')
                  }
                : {
                      ...generateOption('weekly'),
                      ...generateOption('monthly'),
                      ...generateOption('lastDayOfMonth')
                  }
        }
        case 5:
            return {
                ...generateOption('weekly'),
                ...generateOption('lastDayOfMonth')
            }
        default:
            return {
                ...generateOption('weekly'),
                ...generateOption('monthly')
            }
    }
}

/**
 * Converts form values from the event creation form to the payload format required by the API
 * @param formValues - The validated form values from the event creation form
 * @returns A properly formatted event payload object ready for API submission
 */
export const eventFormValuesToCreatePayload = (
    formValues: CreateEventFormConfirmationValidationSchema
): HttpCreateEventOptions['payload'] => {
    if (formValues.isGenericEvent) {
        const consultant = formValues.consultants?.[0] ?? raise('consultant is required')
        return {
            type: 'generic_event',
            note: formValues.note,
            status: 'scheduled',
            eventConsultants: [
                {
                    userId: consultant.id,
                    accessTypeId: null
                }
            ],
            eventPatients: [],
            eventTeamMembers: [],
            sector: 'outpatient',
            date: formatDate(formValues.date?.date ?? raise('date is required')),
            time: formValues.date?.time ?? raise('time is required'),
            duration: formValues.duration ? parseInt(formValues.duration) : 15
        }
    }

    // Non-generic event - these fields are guaranteed by validation
    const patients = formValues.patients ?? raise('patients is required')
    const consultants = formValues.consultants ?? raise('consultants is required')
    const category = formValues.category ?? raise('category is required')
    const date = formValues.date ?? raise('date is required')

    return {
        type: formValues.type,
        category,
        note: formValues.note,
        isRemote: formValues.isRemote || false,
        remoteUrl: formValues.remoteUrl || null,
        isARecovery: formValues.isARecovery ?? false,
        sector: 'outpatient',
        eventTeamMembers:
            category == 'team_meeting' && formValues.eventTeamMembers
                ? formValues.eventTeamMembers.map(teamMember => ({
                      treatmentRequestTeamId: teamMember.id
                  }))
                : [],
        duration: formValues.duration ? parseInt(formValues.duration) : 15,
        status: formValues.status ?? 'scheduled',
        eventPatients: patients.map(patient => ({
            patientId: patient.id,
            projectId: patient.project?.id
        })),
        eventConsultants: consultants.map(consultant => ({
            userId: consultant.id,
            accessTypeId: consultant.accessType?.id ?? raise('access type is required')
        })),
        date: formatDate(date.date ?? raise('date.date is required')),
        time: date.time ?? raise('date.time is required'),
        location: date.location ?? raise('date.location is required'),
        isGroupTreatment: formValues.isGroupTreatment,
        notificationRecipientIds: formValues.notificationsRecipients?.map(recipient => recipient.id),
        notificationDetails: formValues.notificationDetails,
        missedAccessId: formValues.missedAccess?.id,
        isRepeated: category == 'treatments' ? formValues.isRepeated ?? false : false,
        frequency: category == 'treatments' ? formValues.repetitionData?.frequency : undefined,
        position: category == 'treatments' ? formValues.repetitionData?.position : undefined,
        dayOfWeek: category == 'treatments' ? formValues.repetitionData?.dayOfWeek : undefined
    }
}

/**
 * Converts form values from the event creation form to the payload format required by the API
 * @param formValues - The validated form values from the event creation form
 * @returns A properly formatted event payload object ready for API submission
 */
export const eventFormValuesToUpdatePayload = (
    formValues: EditEventFormConfirmationValidationSchema,
    {
        isRecurringEvent,
        isDisabledBulkOperation,
        applyToFutureEvents,
        isChangedSubstitute,
        openCycleAccessType
    }: {
        isRecurringEvent: boolean
        isDisabledBulkOperation: boolean
        applyToFutureEvents: boolean
        isChangedSubstitute: boolean
        openCycleAccessType?: AccessType
    }
): HttpEditEventOptions['payload'] => {
    return {
        type: formValues.type,
        category: formValues.category,
        note: formValues.note,
        isRemote: formValues.isRemote ?? false,
        remoteUrl: formValues.remoteUrl || null,
        isARecovery: formValues.isARecovery ?? false,
        missedAccessId: formValues.missedAccess?.id,
        sector: 'outpatient',
        eventTeamMembers:
            formValues.category == 'team_meeting' && formValues.eventTeamMembers
                ? formValues.eventTeamMembers.map(teamMember => ({
                      treatmentRequestTeamId: teamMember.id
                  }))
                : [],
        duration: formValues.duration ? parseInt(formValues.duration) : 15,
        status: formValues.status ?? 'scheduled',
        eventPatients: formValues.patients.map(patient => ({
            patientId: patient.id,
            projectId: patient.project?.id
        })),
        eventConsultants: formValues.consultants.map(consultant => ({
            userId: consultant.id,
            accessTypeId:
                consultant.accessType?.accessType == 'project_opening_processing' &&
                formValues.patients?.[0].project?.type == 'cyclic'
                    ? openCycleAccessType?.id ?? consultant.accessType?.id ?? raise('access type is required')
                    : consultant.accessType?.id ?? raise('access type is required'),
            substituteId: formValues.substitute?.id ?? null
        })),
        date: formatDate(formValues.date.date),
        time: formValues.date?.time,
        location: formValues.date?.location,
        notificationDetails: formValues.notificationDetails,
        notificationRecipientIds: formValues.notificationsRecipients?.map(recipient => recipient.id),
        applyToFutureEvents:
            formValues.type == 'event' && isRecurringEvent && !isDisabledBulkOperation && !isChangedSubstitute
                ? applyToFutureEvents
                : undefined,
        applySubstituteToFutureEvents:
            formValues.type == 'event' && isRecurringEvent && !isDisabledBulkOperation && isChangedSubstitute
                ? applyToFutureEvents
                : undefined,
        frequency:
            formValues.type == 'event' &&
            formValues.category == 'treatments' &&
            isRecurringEvent &&
            !isDisabledBulkOperation
                ? formValues.repetitionData?.frequency
                : undefined,
        position:
            formValues.type == 'event' &&
            formValues.category == 'treatments' &&
            isRecurringEvent &&
            !isDisabledBulkOperation
                ? formValues.repetitionData?.position
                : undefined,
        dayOfWeek:
            formValues.type == 'event' &&
            formValues.category == 'treatments' &&
            isRecurringEvent &&
            !isDisabledBulkOperation
                ? formValues.repetitionData?.dayOfWeek
                : undefined
    }
}

/**
 * Converts generic event form values to API payload format for update
 * @param formValues - The validated form values from the generic event edit form
 * @returns A properly formatted generic event payload object ready for API submission
 */
export const genericEventFormValuesToUpdatePayload = (
    formValues: EditGenericEventFormConfirmationValidationSchema
): HttpEditEventOptions['payload'] => {
    return {
        type: 'generic_event',
        note: formValues.note,
        status: formValues.status,
        eventConsultants: [
            {
                userId: formValues.consultants[0].id,
                accessTypeId: null
            }
        ],
        date: formatDate(formValues.date.date),
        time: formValues.date.time,
        duration: parseInt(formValues.duration),
        eventPatients: [],
        eventTeamMembers: [],
        sector: 'outpatient',
        notificationDetails: formValues.notificationDetails,
        notificationRecipientIds: formValues.notificationsRecipients?.map(recipient => recipient.id)
    }
}

/**
 * Converts patient absence form values to API payload format
 *
 * @description
 * Takes validated patient absence form data and transforms it into a single API-ready payload object.
 * The function handles:
 * - Formatting dates and calculating number of days
 * - Proper formatting of patient and project IDs
 * - Communication timing and attachment details
 * - Notification recipient formatting
 *
 * @param formValues - The validated form values from the patient absence form
 * @returns A properly formatted patient absence payload object ready for API submission
 *
 * @example
 * // Example usage:
 * const payload = patientAbsenceFormValuesToPayload({
 *   startDate: '2023-05-10',
 *   endDate: '2023-05-12',
 *   patients: [{ id: 'patient-123', project: { id: 'project-456' }}],
 *   communicatedOnTime: 'within_48_hours',
 *   notificationsRecipients: [{ id: 'user-789' }]
 * });
 */
export const patientAbsenceFormValuesToPayload = (formValues: AddPatientAbsenceConfirmationValidationSchema) => {
    const numberOfDays = dayjs(formValues.endDate).diff(dayjs(formValues.startDate), 'days') + 1

    const payload: EventPayload = {
        date: formatDate(formValues.startDate),
        type: 'patient_absence',
        eventPatients: formValues.patients.map(patient => ({
            patientId: patient.id,
            projectId: patient.project?.id
        })),
        communicatedOnTime: formValues.communicatedOnTime
            ? formValues.communicatedOnTime === 'over_48_hours'
                ? true
                : false
            : null,
        attachmentId: formValues.communicatedAttachemnts?.id ?? null,
        eventTeamMembers: [],
        eventConsultants: [],
        sector: 'outpatient',
        notificationRecipientIds: formValues.notificationsRecipients?.map(recipient => recipient.id),
        notificationDetails: formValues.notificationDetails,
        numberOfDays: numberOfDays
    }

    return payload
}

/**
 * Converts consultant absence form values to API payload format
 *
 * @description
 * Takes validated consultant absence form data and transforms it into a single API-ready payload object.
 * The function handles:
 * - Calculating number of days between start and end dates
 * - Formatting time and duration values
 * - Proper formatting of all required API payload fields including consultant, notifications and absence details
 *
 * @param formValues - The validated form values from the consultant absence form
 * @returns A properly formatted consultant absence payload object ready for API submission
 *
 * @example
 * // Example usage:
 * const payload = consultantAbsenceFormValuesToPayload({
 *   startDate: '2023-05-10',
 *   endDate: '2023-05-12',
 *   startTime: '09:00',
 *   endTime: '13:00',
 *   consultant: { id: 'consultant-123', accessType: { id: 'access-456' } },
 *   absenceReason: 'personal_reasons'
 * });
 */
export const consultantAbsenceFormValuesToPayload = (formValues: AddConsultantAbsenceConfirmationValidationSchema) => {
    const numberOfDays = dayjs(formValues.endDate).diff(dayjs(formValues.startDate), 'days') + 1
    const getTimeDuration = (): {time: string; duration: number} => {
        // Handle middle dates - full day absence
        return {
            time: formValues.startTime ?? DEFAULT_START_TIME,
            duration: Math.abs(
                dayjs(`2000-01-01 ${formValues.startTime ?? DEFAULT_START_TIME}`).diff(
                    dayjs(`2000-01-01 ${formValues.endTime ?? DEFAULT_END_TIME}`),
                    'minutes'
                )
            )
        }
    }

    const timeDuration = getTimeDuration()
    const payload: EventPayload = {
        date: formatDate(formValues.startDate),
        type: 'consultant_absence',
        eventPatients: [],
        eventTeamMembers: [],
        eventConsultants: formValues.consultant
            ? [
                  {
                      userId: formValues.consultant.id,
                      accessTypeId: formValues.consultant.accessType?.id ?? ''
                  }
              ]
            : [],
        absenceReason: formValues.absenceReason,
        sector: 'outpatient',
        time: timeDuration.time,
        duration: timeDuration.duration,
        notificationRecipientIds: formValues.notificationsRecipients?.map(recipient => recipient.id),
        notificationDetails: formValues.notificationDetails,
        numberOfDays: numberOfDays
    }

    return payload
}

/**
 * Filter available time slots based on duration
 */
export const filterAvailableTimeSlots = (times: string[], duration: string): string[] => {
    if (!times.length || !duration) return []

    return times.filter((_, index) => {
        // Check if we have enough consecutive slots for the selected duration
        const durationInMinutes = parseInt(duration)
        const requiredSlots = Math.ceil(durationInMinutes / 15)

        // Check if there are enough slots after this one
        if (index + requiredSlots > times.length) return false

        // Check if the next required slots are consecutive
        for (let i = 1; i < requiredSlots; i++) {
            const currentSlot = times[index]
            const nextSlot = times[index + i]

            const currentTime = dayjs(`2000-01-01 ${currentSlot}`)
            const expectedNextTime = currentTime.add(i * 15, 'minute').format('HH:mm')

            // If the next slot doesn't match the expected time (15min increments), return false
            if (nextSlot !== expectedNextTime) {
                return false
            }
        }

        return true
    })
}

/**
 * Calculates which dates should be disabled in the calendar based on available slots and duration
 */
export const getDisabledDates = (availabilities: Record<string, string[]>, duration?: string): Date[] => {
    if (!availabilities || Object.keys(availabilities).length === 0 || !duration) return []

    const dates = ObjectKeys(availabilities)
    return dates
        .filter(date => {
            const times = availabilities[date]

            // If no time slots available at all, disable the date
            if (times?.length === 0) return true

            // Check if any time slot can accommodate the selected duration
            const hasValidSlot = filterAvailableTimeSlots(times, duration).length > 0

            // If no valid slots for the duration, disable the date
            return !hasValidSlot
        })
        .map(date => dayjs(date).toDate())
}

/**
 * Extracts the unavailable slots from availabilities for a specific date
 * @param availabilities - Record of availabilities by date
 * @param targetDate - The date to get unavailable slots for
 * @returns Array of unavailable time slots with start and end times as Date objects
 */
export const getUnavailableSlots = (
    availabilities: Record<string, string[]>,
    targetDate: Date
): Array<{start: Date; end: Date}> => {
    if (!availabilities || Object.keys(availabilities).length === 0 || !targetDate) return []

    const targetDateStr = formatDate(targetDate)
    const dateSlotKey = Object.keys(availabilities).find(a => formatDate(a) === targetDateStr)

    if (!dateSlotKey) return []

    const availableSlots = availabilities[dateSlotKey]
    if (!availableSlots.length) {
        // Return full day unavailability
        return [
            {
                start: dayjs(`${targetDateStr} 08:00`).toDate(),
                end: dayjs(`${targetDateStr} 18:45`).toDate()
            }
        ]
    }

    const unavailableSlots: Array<{start: Date; end: Date}> = []

    // Create a set of all 15-minute intervals from 08:00 to 18:45
    const allTimeSlots: string[] = []
    let currentTime = dayjs('2000-01-01 08:00')
    const endTime = dayjs('2000-01-01 18:45')

    while (currentTime.isBefore(endTime) || currentTime.isSame(endTime)) {
        allTimeSlots.push(currentTime.format('HH:mm'))
        currentTime = currentTime.add(15, 'minute')
    }

    // Find gaps in availability
    let start: string | null = null

    for (let i = 0; i < allTimeSlots.length; i++) {
        const timeSlot = allTimeSlots[i]
        const isAvailable = availableSlots.includes(timeSlot)

        if (!isAvailable && start === null) {
            // Start of unavailable period
            start = timeSlot
        } else if (isAvailable && start !== null) {
            // End of unavailable period
            unavailableSlots.push({
                start: dayjs(`${targetDateStr} ${start}`).toDate(),
                end: dayjs(`${targetDateStr} ${timeSlot}`).toDate()
            })
            start = null
        }
    }

    // Handle case where last slot is unavailable
    if (start !== null) {
        unavailableSlots.push({
            start: dayjs(`${targetDateStr} ${start}`).toDate(),
            end: dayjs(`${targetDateStr} 20:00`).toDate()
        })
    }

    return unavailableSlots
}

export const accessGroupNameEnumToLabel = {
    [AccessGroupNameEnum.enum.in_person_accesses]: 'commons:single_accesses',
    [AccessGroupNameEnum.enum.non_in_person_accesses]: 'projects:absences_accesses',
    [AccessGroupNameEnum.enum.treatments]: 'projects:treatments'
} as const satisfies Record<AccessGroupNameEnum, TKey>
